<template>
  <span class="job-status">
    <span class="job-status__chip" :class="`job-status__chip--${modifier}`">
      <v-progress-circular v-if="showSpinner" indeterminate :size="12" :width="2" class="job-status__spinner" />
      <v-icon v-else :size="13" class="job-status__icon">{{ icon }}</v-icon>
      {{ label }}
    </span>
    <!-- Spec: the stale copy is visible in the status cell, not hover-only; one line, truncated with a tooltip (rule 05). -->
    <span v-if="isStale" class="job-status__stale" :title="staleMessage">{{ staleMessage }}</span>
  </span>
</template>

<script>
  import { JOB_UI_STATUS, STALE_RUNNING_MESSAGE, isStaleRunning } from '../../utils/findingsMetrics';

  const PRESENTATION = Object.freeze({
    [JOB_UI_STATUS.RUNNING]: { modifier: 'running', icon: 'mdi-progress-clock', label: 'Running' },
    [JOB_UI_STATUS.DONE]: { modifier: 'done', icon: 'mdi-check-circle', label: 'Done' },
    [JOB_UI_STATUS.FAILED]: { modifier: 'failed', icon: 'mdi-alert-circle', label: 'Failed' },
    [JOB_UI_STATUS.CANCELLED]: { modifier: 'cancelled', icon: 'mdi-cancel', label: 'Cancelled' },
  });
  const STALE = Object.freeze({ modifier: 'stale', icon: 'mdi-clock-alert-outline', label: 'Running' });

  export default {
    name: 'JobStatusChip',
    props: {
      job: { type: Object, required: true },
    },
    data() {
      return { staleMessage: STALE_RUNNING_MESSAGE };
    },
    computed: {
      isStale() {
        // Re-evaluated on every poll: the jobs array is replaced, so the row receives a new job object.
        return isStaleRunning(this.job);
      },
      presentation() {
        return this.isStale ? STALE : PRESENTATION[this.job.uiStatus] || PRESENTATION[JOB_UI_STATUS.CANCELLED];
      },
      showSpinner() {
        return this.job.uiStatus === JOB_UI_STATUS.RUNNING && !this.isStale;
      },
      modifier() {
        return this.presentation.modifier;
      },
      icon() {
        return this.presentation.icon;
      },
      label() {
        return this.presentation.label;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .job-status {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    max-width: 100%;
    vertical-align: middle;
  }

  .job-status__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 22px;
    padding: 0 9px;
    font-size: var(--aa-grid-font-secondary);
    font-weight: 600;
    letter-spacing: 0.01em;
    line-height: 1;
    border-radius: var(--ds-radius-full);
    white-space: nowrap;

    &--running { color: var(--aa-status-running); background: var(--aa-status-running-bg); }
    &--done { color: var(--aa-status-done); background: var(--aa-status-done-bg); }
    &--failed { color: var(--aa-status-failed); background: var(--aa-status-failed-bg); }
    &--cancelled { color: var(--aa-status-cancelled); background: var(--aa-status-cancelled-bg); }
    &--stale { color: var(--aa-status-stale); background: var(--aa-status-stale-bg); }
  }

  // Vuetify paints .v-icon in the theme grey; the chip colour must win.
  .job-status__icon,
  .job-status__spinner {
    color: inherit !important;
  }

  .job-status__stale {
    display: block;
    max-width: 100%;
    margin-top: 2px;
    font-size: var(--aa-grid-font-secondary);
    line-height: 1.3;
    color: var(--aa-status-stale);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
